/**
 * Product Metafield Rows JavaScript - Grouped JSON Implementation
 *
 * Handles dynamic content updates for grouped product specifications.
 * Streamlined for single JSON metafield with category-based organization.
 * Maintains security, accessibility, and performance features.
 */

// Guard against re-declaration when script loads multiple times (e.g., in quickview)
if (typeof window.GroupedProductSpecifications === 'undefined') {

class GroupedProductSpecifications {
  constructor() {
    // Find grouped specifications containers
    this.container = document.querySelector('.grouped-specifications');
    if (!this.container) {
      return;
    }

    // Initialize settings and state
    this.initSettings();
    this.initState();
    this.init();
  }

  initSettings() {
    // Read theme settings from Shopify settings or fallback to defaults
    this.settings = {
      enableAnimations: window.themeSettings?.enable_spec_animations !== 'false',
      enableCopyButtons: this.container.querySelector('.spec-copy-btn') !== null,
      collapsibleCategories: this.container.querySelector('.category-toggle') !== null,
      mobileLayout: window.themeSettings?.mobile_spec_layout || 'accordion'
    };

    // Fallback to reading from CSS custom properties if themeSettings not available
    if (!window.themeSettings) {
      const root = document.documentElement;
      this.settings.enableAnimations = 
        getComputedStyle(root).getPropertyValue('--enable-spec-animations')?.trim() !== 'false';
    }
  }

  initState() {
    // Current variant tracking
    this.currentVariantId = this.getCurrentVariantId();
    
    // Cache for parsed specification data
    this.specificationsCache = new Map();
    
    // Debouncing and update control
    this.updateTimeout = null;
    this.isUpdating = false;

    // Category containers
    this.categories = this.container.querySelectorAll('.product-specifications-category');
  }

  init() {
    if (!this.container || this.categories.length === 0) {
      return;
    }

    // Load initial data into cache
    this.loadSpecificationsData();

    // Set up event listeners
    this.setupEventListeners();

    // Initialize interactive features
    this.initCollapsibleCategories();
    this.initCopyButtons();

    // Force table widths to 100% (fix for CSS specificity issues)
    this.forceTableWidths();

    // Convert decimal dimensions to fractions on initial load
    this.convertInitialDimensions();

    // Set up accessibility
    this.initAccessibility();
  }

  /**
   * Convert decimal dimension values to fractions on initial page load
   * This handles values rendered by Liquid before JavaScript takes over
   */
  convertInitialDimensions() {
    // Target both main product page (.specification-value) and quickview (.spec-value) cells
    const valuesCells = this.container.querySelectorAll('.specification-value, .spec-value');
    valuesCells.forEach(cell => {
      const originalText = cell.textContent?.trim();
      if (originalText) {
        const result = this.formatDimensionValue(originalText);
        // Handle both string and object (HTML) results
        if (result && typeof result === 'object' && result.html) {
          cell.innerHTML = result.value;
        } else if (result !== originalText) {
          cell.textContent = result;
        }
      }
    });
  }

  getCurrentVariantId() {
    // Try multiple sources for variant ID
    const sources = [
      () => document.querySelector('[data-product-variants]')?.dataset?.selectedVariant,
      () => document.querySelector('input[name="id"]:checked')?.value,
      () => document.querySelector('select[name="id"]')?.value,
      () => window.product?.selected_or_first_available_variant?.id,
      () => document.querySelector('[data-variant-id]')?.dataset?.variantId
    ];

    for (const source of sources) {
      try {
        const id = source();
        if (id && id !== 'null' && id !== 'undefined') {
          return parseInt(id, 10);
        }
      } catch (_error) {
        // Continue to next source
      }
    }

    return null;
  }

  loadSpecificationsData() {
    // Load data from embedded JSON scripts
    const dataScripts = this.container.querySelectorAll('script[data-specifications-data]');
    
    dataScripts.forEach(script => {
      try {
        const category = script.dataset.category;
        const data = JSON.parse(script.textContent);
        
        if (category && data) {
          this.specificationsCache.set(category, data);
        }
      } catch (error) {
        // Development mode debug logging
        if (window.themeSettings?.debug || window.debugMode) {
          console.warn(`Failed to parse specifications data for category:`, error);
        }
      }
    });
  }

  setupEventListeners() {
    // Listen for variant changes from various sources
    document.addEventListener('variant:changed', this.handleVariantChanged.bind(this));
    document.addEventListener('change', this.handleFormChange.bind(this));
    
    // Listen for product form changes as fallback
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      productForm.addEventListener('change', this.debounceVariantUpdate.bind(this));
    }

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      this.specificationsCache.clear();
    });
  }

  handleVariantChanged(event) {
    const newVariantId = event.detail?.variant?.id || event.detail?.id;
    if (newVariantId && newVariantId !== this.currentVariantId) {
      this.updateSpecifications(newVariantId);
    }
  }

  handleFormChange(event) {
    if (event.target && (event.target.name === 'id' || event.target.dataset.variantSelector)) {
      this.debounceVariantUpdate();
    }
  }

  debounceVariantUpdate() {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    this.updateTimeout = setTimeout(() => {
      const newVariantId = this.getCurrentVariantId();
      if (newVariantId && newVariantId !== this.currentVariantId) {
        this.updateSpecifications(newVariantId);
      }
    }, 150);
  }

  async updateSpecifications(variantId) {
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;
    
    try {
      // Update each category
      for (const categoryContainer of this.categories) {
        await this.updateCategory(categoryContainer, variantId);
      }
      
      this.currentVariantId = variantId;
      
      // Announce change for screen readers
      this.announceSpecificationUpdate();
      
    } catch (error) {
      // Log error for development debugging
      if (window.themeSettings?.debug || window.debugMode) {
        console.error('Failed to update specifications:', error);
      }
      this.showUpdateError();
    } finally {
      this.isUpdating = false;
    }
  }

  async updateCategory(categoryContainer, variantId) {
    const category = categoryContainer.dataset.category;
    const contentArea = categoryContainer.querySelector('.category-content');
    const tableContainer = contentArea?.querySelector('.specifications-table-container');
    
    if (!category || !contentArea || !tableContainer) {
      return;
    }

    // Get cached data for this category
    const categoryData = this.specificationsCache.get(category);
    const variantSpecs = categoryData?.[variantId];
    
    if (!variantSpecs) {
      // No data for this variant - hide category
      this.hideCategoryGracefully(categoryContainer);
      return;
    }

    // Show loading state if animations enabled
    if (this.settings.enableAnimations) {
      contentArea.style.opacity = '0.5';
      contentArea.setAttribute('aria-busy', 'true');
    }

    // Build new table content using safe DOM methods
    const tableFragment = this.buildSpecificationsTableFragment(variantSpecs);
    
    // Update table content safely
    const table = tableContainer.querySelector('.specifications-table tbody');
    if (table) {
      table.replaceChildren(tableFragment);
    }

    // Show the category if it was hidden
    this.showCategory(categoryContainer);

    // Restore visual state
    if (this.settings.enableAnimations) {
      await this.animateContentUpdate(contentArea);
    }
    
    contentArea.setAttribute('aria-busy', 'false');
    contentArea.setAttribute('data-variant-id', variantId);

    // Re-initialize copy buttons for new content
    this.initCopyButtonsInCategory(categoryContainer);
  }

  buildSpecificationsTableFragment(specifications) {
    const fragment = document.createDocumentFragment();
    
    // Sort specifications by position if available
    const sortedSpecs = Object.entries(specifications).sort((a, b) => {
      const positionA = a[1]?.position || 999;
      const positionB = b[1]?.position || 999;
      return positionA - positionB;
    });
    
    for (const [specKey, specData] of sortedSpecs) {
      if (!specData?.value) continue;
      
      const row = this.createSpecificationRow(specKey, specData);
      fragment.appendChild(row);
    }
    
    return fragment;
  }

  /**
   * Unicode vulgar fractions map - these render properly in all fonts
   */
  getVulgarFraction(num, denom) {
    const fractionMap = {
      '1/2': '½',
      '1/4': '¼', '3/4': '¾',
      '1/8': '⅛', '3/8': '⅜', '5/8': '⅝', '7/8': '⅞',
      '1/3': '⅓', '2/3': '⅔',
      '1/5': '⅕', '2/5': '⅖', '3/5': '⅗', '4/5': '⅘',
      '1/6': '⅙', '5/6': '⅚',
    };
    return fractionMap[`${num}/${denom}`] || null;
  }

  /**
   * Reduce a fraction to its simplest form
   * Returns HTML span with fraction class for Inter font styling
   */
  reduceFraction(num, denom) {
    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const divisor = gcd(num, denom);
    const reducedNum = num / divisor;
    const reducedDenom = denom / divisor;

    // Return HTML span - Inter font's "frac" feature styles it automatically
    return { html: true, value: `<span class="fraction">${reducedNum}/${reducedDenom}</span>` };
  }

  /**
   * Convert a single number with optional inch mark to fraction format
   * Converts to fractions up to 64ths precision
   * Returns {html: true, value: string} for styled fractions
   * @private
   */
  convertSingleDimension(numStr, inchMark = '') {
    const num = parseFloat(numStr);
    if (isNaN(num)) return numStr + inchMark;

    const whole = Math.floor(num);
    const decimal = num - whole;

    // Less than 1/128, treat as whole number
    if (decimal < 0.0078125) {
      return whole + inchMark;
    }

    // Round to nearest 64th
    const sixtyfourths = Math.round(decimal * 64);

    // Handle case where rounding gives 64/64 (i.e., add 1 to whole)
    if (sixtyfourths === 64) {
      return (whole + 1) + inchMark;
    }

    // Get the fraction (HTML object with span wrapper)
    const fraction = this.reduceFraction(sixtyfourths, 64);

    if (whole === 0) {
      return { html: true, value: fraction.value + inchMark };
    }
    // Use thin space (U+2009) between whole number and fraction
    return { html: true, value: whole + '\u2009' + fraction.value + inchMark };
  }

  /**
   * Convert decimal dimension values to American fractions (1/64" precision)
   * Handles single values like "16.87"" and compound values like "25.25 x 25.75"
   * e.g., "16.87"" becomes "16 7/8"", "25.25 x 25.75" becomes "25 1/4 x 25 3/4"
   * Skips weight values (lbs, oz, kg, g) - only converts inch dimensions
   * Returns string or {html: true, value: string} for styled fractions
   */
  formatDimensionValue(value) {
    if (typeof value !== 'string') return value;

    // Skip weight values - don't convert lbs, oz, kg, g to fractions
    // Return as-is without subscript styling
    const valueLower = value.toLowerCase();
    if (valueLower.includes('lb') || valueLower.includes('oz') ||
        valueLower.includes('kg') || valueLower.includes(' g') ||
        valueLower.endsWith('g')) {
      return value;
    }

    // Helper to extract value from string or HTML object
    const getValue = (v) => (v && typeof v === 'object' && v.html) ? v.value : v;
    const isHtml = (v) => v && typeof v === 'object' && v.html;

    // Check for compound dimensions (e.g., "25.25 x 25.75" or "5 x 4.5 x 0.5"")
    // Handle 2, 3, or more dimensions separated by x/X/×
    if (/[xX×]/.test(value)) {
      // Extract trailing inch mark if present (e.g., '5 x 4.5 x 0.5"' -> '"')
      const trailingInchMatch = value.match(/[""]$/);
      const trailingInch = trailingInchMatch ? trailingInchMatch[0] : '';
      const valueWithoutTrailing = trailingInch ? value.slice(0, -1) : value;

      // Split by separator, preserving the separator for reconstruction
      const parts = valueWithoutTrailing.split(/(\s*[xX×]\s*)/);
      const convertedParts = [];
      let hasHtml = false;

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        // Odd indices are separators, even indices are dimension values
        if (i % 2 === 0) {
          // This is a dimension value - extract number and optional inch mark
          const dimMatch = part.match(/^([\d.]+)(\s*[""]?)$/);
          if (dimMatch) {
            const converted = this.convertSingleDimension(dimMatch[1], dimMatch[2]);
            if (isHtml(converted)) hasHtml = true;
            convertedParts.push(converted);
          } else {
            convertedParts.push(part);
          }
        } else {
          // This is a separator (x, X, or ×)
          convertedParts.push(part);
        }
      }

      // Reconstruct the string
      const result = convertedParts.map(getValue).join('') + trailingInch;
      return hasHtml ? { html: true, value: result } : result;
    }

    // Single dimension pattern: number (with optional decimal) followed by optional inch mark
    const match = value.match(/^([\d.]+)(\s*[""]?)(.*)$/);
    if (!match) return value;

    const result = this.convertSingleDimension(match[1], match[2]);
    const suffix = match[3] || '';

    // If result is HTML, append suffix to the HTML value
    if (isHtml(result)) {
      return { html: true, value: result.value + suffix };
    }
    return result + suffix;
  }

  createSpecificationRow(specKey, specData) {
    const name = specData.name || specKey.replace(/_/g, ' ');
    const value = specData.value;
    const tooltip = specData.tooltip;
    
    // Create row element
    const row = document.createElement('tr');
    row.className = 'specification-row';
    row.setAttribute('data-spec-key', specKey);
    
    // Create label cell
    const labelCell = document.createElement('td');
    labelCell.className = 'specification-label';
    labelCell.textContent = name;
    
    // Add tooltip if available
    if (tooltip) {
      const tooltipSpan = document.createElement('span');
      tooltipSpan.className = 'specification-tooltip';
      tooltipSpan.title = tooltip;
      tooltipSpan.setAttribute('tabindex', '0');
      tooltipSpan.setAttribute('role', 'button');
      tooltipSpan.setAttribute('aria-label', `More information: ${tooltip}`);
      tooltipSpan.innerHTML = `
        <svg class="icon icon-help-circle" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="m9,9a3,3 0 1,1 5.03,2.13 13.27,13.27 0 0,1-2.53,4.12l0,0.75"></path>
          <circle cx="12" cy="17" r="1"></circle>
        </svg>
      `;
      labelCell.appendChild(tooltipSpan);
    }
    
    // Create value cell
    const valueCell = document.createElement('td');
    valueCell.className = 'specification-value';
    // Convert decimal dimensions to American fractions
    const formattedValue = this.formatDimensionValue(value);
    if (formattedValue && typeof formattedValue === 'object' && formattedValue.html) {
      valueCell.innerHTML = formattedValue.value;
    } else {
      valueCell.textContent = formattedValue;
    }
    
    // Add copy button if enabled
    if (this.settings.enableCopyButtons) {
      const copyButton = document.createElement('button');
      copyButton.type = 'button';
      copyButton.className = 'spec-copy-btn';
      copyButton.setAttribute('data-copy-text', value);
      copyButton.title = `Copy ${name}`;
      copyButton.setAttribute('aria-label', `Copy ${name} value: ${value}`);
      copyButton.innerHTML = `
        <svg class="icon icon-copy" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="m5,15l0,-6a2,2 0 0,1 2,-2l6,0"></path>
        </svg>
      `;
      valueCell.appendChild(copyButton);
    }
    
    row.appendChild(labelCell);
    row.appendChild(valueCell);
    
    return row;
  }

  hideCategoryGracefully(categoryContainer) {
    if (this.settings.enableAnimations) {
      categoryContainer.style.opacity = '0';
      categoryContainer.style.height = '0';
      categoryContainer.style.overflow = 'hidden';
      categoryContainer.setAttribute('aria-hidden', 'true');
    } else {
      categoryContainer.style.display = 'none';
    }
  }

  showCategory(categoryContainer) {
    categoryContainer.style.display = '';
    categoryContainer.style.opacity = '';
    categoryContainer.style.height = '';
    categoryContainer.style.overflow = '';
    categoryContainer.setAttribute('aria-hidden', 'false');
  }

  async animateContentUpdate(contentArea) {
    return new Promise(resolve => {
      setTimeout(() => {
        contentArea.style.opacity = '1';
        resolve();
      }, 100);
    });
  }

  initCollapsibleCategories() {
    if (!this.settings.collapsibleCategories) {
      return;
    }

    const toggleButtons = this.container.querySelectorAll('.category-toggle');
    
    toggleButtons.forEach(button => {
      button.addEventListener('click', this.handleCategoryToggle.bind(this));
      button.addEventListener('keydown', this.handleCategoryToggle.bind(this));
    });
  }

  handleCategoryToggle(event) {
    // Handle both click and keyboard events
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    
    event.preventDefault(); // Prevent space key scrolling
    
    const button = event.currentTarget;
    const categoryContainer = button.closest('.product-specifications-category');
    const content = categoryContainer?.querySelector('.category-content');
    
    if (!categoryContainer || !content) {
      return;
    }

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;
    
    // Update ARIA state
    button.setAttribute('aria-expanded', newState);
    
    // Toggle content visibility
    if (this.settings.enableAnimations) {
      if (newState) {
        content.style.display = 'block';
        content.style.opacity = '0';
        content.style.height = '0';
        
        requestAnimationFrame(() => {
          content.style.opacity = '1';
          content.style.height = 'auto';
        });
      } else {
        content.style.opacity = '0';
        content.style.height = '0';
        
        setTimeout(() => {
          content.style.display = 'none';
        }, 300);
      }
    } else {
      content.style.display = newState ? 'block' : 'none';
    }

    // Update button icon rotation
    const icon = button.querySelector('.icon');
    if (icon) {
      icon.style.transform = newState ? 'rotate(0deg)' : 'rotate(-90deg)';
    }
  }

  initCopyButtons() {
    this.initCopyButtonsInCategory(this.container);
  }

  initCopyButtonsInCategory(container) {
    if (!this.settings.enableCopyButtons) {
      return;
    }

    const copyButtons = container.querySelectorAll('.spec-copy-btn');
    
    copyButtons.forEach(button => {
      // Remove existing bound listeners if they exist
      if (button._boundCopyHandler) {
        button.removeEventListener('click', button._boundCopyHandler);
        button.removeEventListener('keydown', button._boundCopyHandler);
      }
      
      // Create and store bound handler to prevent memory leaks
      button._boundCopyHandler = this.handleCopyClick.bind(this);
      button.addEventListener('click', button._boundCopyHandler);
      button.addEventListener('keydown', button._boundCopyHandler);
    });
  }

  async handleCopyClick(event) {
    // Handle both click and keyboard events
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    
    event.preventDefault();
    
    const button = event.currentTarget;
    const textToCopy = button.dataset.copyText;
    
    if (!textToCopy) {
      return;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      
      // Show success feedback
      this.showCopySuccess(button);
      
    } catch (_error) {
      // Fallback for older browsers
      this.fallbackCopyText(textToCopy);
      this.showCopySuccess(button);
    }
  }

  fallbackCopyText(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  showCopySuccess(button) {
    const originalHTML = button.innerHTML;
    const originalLabel = button.getAttribute('aria-label');
    
    // Update visual and screen reader feedback
    button.innerHTML = `
      <svg class="icon icon-check" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <polyline points="20,6 9,17 4,12"></polyline>
      </svg>
    `;
    
    button.setAttribute('aria-label', 'Copied successfully');
    button.classList.add('copied');
    
    // Announce to screen readers
    this.announceSuccess('Value copied to clipboard');
    
    setTimeout(() => {
      button.innerHTML = originalHTML;
      button.setAttribute('aria-label', originalLabel);
      button.classList.remove('copied');
    }, 2000);
  }

  initAccessibility() {
    // Add ARIA labels and landmark roles
    if (!this.container.getAttribute('role')) {
      this.container.setAttribute('role', 'region');
      this.container.setAttribute('aria-label', 'Product specifications');
    }
    
    // Add ARIA labels and roles for categories
    this.categories.forEach((category, index) => {
      const header = category.querySelector('.metafield-card__header h3, .metafield-card__header h4, .metafield-card__header h5');
      const content = category.querySelector('.category-content');
      const toggle = category.querySelector('.category-toggle');
      
      if (header && content) {
        const headerId = `category-header-${Date.now()}-${index}`;
        const contentId = `category-content-${Date.now()}-${index}`;
        
        if (!header.id) {
          header.id = headerId;
        }
        if (!content.id) {
          content.id = contentId;
        }
        content.setAttribute('aria-labelledby', header.id);
        content.setAttribute('role', 'region');
        
        if (toggle) {
          toggle.setAttribute('aria-controls', content.id);
          toggle.setAttribute('aria-describedby', header.id);
        }
      }
    });

    // Add table accessibility with proper headers
    const tables = this.container.querySelectorAll('.specifications-table');
    tables.forEach((table, index) => {
      if (!table.getAttribute('role')) {
        table.setAttribute('role', 'table');
        const categoryName = table.closest('[data-category-name]')?.dataset.categoryName || 'specifications';
        table.setAttribute('aria-label', `Product ${categoryName.toLowerCase()} specifications`);
      }
      
      // Add row headers if not present
      const rows = table.querySelectorAll('tbody tr');
      rows.forEach((row, rowIndex) => {
        const labelCell = row.querySelector('.specification-label');
        const valueCell = row.querySelector('.specification-value');
        
        if (labelCell && valueCell) {
          const labelId = `spec-label-${index}-${rowIndex}`;
          labelCell.id = labelId;
          labelCell.setAttribute('role', 'rowheader');
          valueCell.setAttribute('aria-describedby', labelId);
        }
      });
    });
  }

  announceSpecificationUpdate() {
    // Create or update screen reader announcement
    let announcer = document.getElementById('specs-update-announcer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'specs-update-announcer';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.style.position = 'absolute';
      announcer.style.left = '-10000px';
      announcer.style.width = '1px';
      announcer.style.height = '1px';
      announcer.style.overflow = 'hidden';
      document.body.appendChild(announcer);
    }

    announcer.textContent = 'Product specifications updated for selected variant.';
    
    // Clear after announcement
    setTimeout(() => {
      announcer.textContent = '';
    }, 1000);
  }

  showUpdateError() {
    // Log error for development debugging  
    if (window.themeSettings?.debug || window.debugMode) {
      console.error('Failed to update product specifications');
    }
    
    // Create accessible error announcement
    this.announceError('Unable to update specifications. Please refresh the page and try again.');
    
    // Show visual error message with proper ARIA attributes
    const errorDiv = document.createElement('div');
    errorDiv.className = 'specifications-error';
    errorDiv.setAttribute('role', 'alert');
    errorDiv.setAttribute('aria-live', 'assertive');
    errorDiv.innerHTML = `
      <p>Unable to update specifications. Please refresh the page and try again.</p>
    `;
    
    this.container.insertBefore(errorDiv, this.container.firstChild);
    
    // Focus the error message for keyboard users
    errorDiv.tabIndex = -1;
    errorDiv.focus();
    
    setTimeout(() => {
      errorDiv.remove();
    }, 10000); // Extended timeout for screen readers
  }

  announceError(message) {
    this.createAnnouncement(message, 'assertive');
  }

  announceSuccess(message) {
    this.createAnnouncement(message, 'polite');
  }

  createAnnouncement(message, priority) {
    let announcer = document.getElementById(`specs-${priority}-announcer`);
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = `specs-${priority}-announcer`;
      announcer.setAttribute('aria-live', priority);
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'sr-only';
      document.body.appendChild(announcer);
    }

    announcer.textContent = message;
    
    // Clear after announcement
    setTimeout(() => {
      announcer.textContent = '';
    }, 1000);
  }

  forceTableWidths() {
    // Force tables to take full width (fixes CSS specificity issues)
    const tables = this.container.querySelectorAll('.specifications-table');
    const containers = this.container.querySelectorAll('.specifications-table-container');
    
    // Force container widths
    containers.forEach(container => {
      container.style.setProperty('width', '100%', 'important');
      container.style.setProperty('min-width', '100%', 'important');
      container.style.setProperty('max-width', '100%', 'important');
      container.style.setProperty('box-sizing', 'border-box', 'important');
    });
    
    // Force table widths
    tables.forEach(table => {
      table.style.setProperty('width', '100%', 'important');
      table.style.setProperty('min-width', '100%', 'important');
      table.style.setProperty('max-width', '100%', 'important');
      table.style.setProperty('table-layout', 'fixed', 'important');
      table.style.setProperty('box-sizing', 'border-box', 'important');
      
      // Force table cells to 50% width each
      const cells = table.querySelectorAll('.specification-label, .specification-value');
      cells.forEach(cell => {
        cell.style.setProperty('width', '50%', 'important');
        cell.style.setProperty('max-width', '50%', 'important');
        cell.style.setProperty('box-sizing', 'border-box', 'important');
      });
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

/**
 * Collapsible State Preserver
 * Preserves open/closed state of <details> elements across variant changes
 *
 * Handles Shopify's section rendering which replaces the entire specs container.
 * Uses document-level event delegation and re-queries DOM after mutations.
 */
class CollapsibleStatePreserver {
  constructor() {
    this.openCategories = new Set();
    this.containerSelector = '.grouped-specifications';
    this.isRestoring = false;

    // Check if container exists
    if (!this.getContainer()) return;

    this.init();
  }

  getContainer() {
    return document.querySelector(this.containerSelector);
  }

  init() {
    // Track initial open states
    this.captureOpenStates();

    // Use document-level event delegation for toggle events
    // This persists even when the container is replaced
    document.addEventListener('toggle', this.handleToggle.bind(this), true);

    // Listen for variant changes from multiple sources
    document.addEventListener('variant:changed', this.handleVariantChange.bind(this));
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));

    // Observe from document body to catch section replacements
    this.setupMutationObserver();
  }

  captureOpenStates() {
    const container = this.getContainer();
    if (!container) return;

    const details = container.querySelectorAll('details[data-category]');
    details.forEach(detail => {
      const category = detail.dataset.category;
      if (detail.open) {
        this.openCategories.add(category);
      } else {
        this.openCategories.delete(category);
      }
    });
  }

  handleToggle(event) {
    // Ignore if we're in the middle of restoring states
    if (this.isRestoring) return;

    if (event.target.tagName !== 'DETAILS') return;

    // Only handle details inside our container
    const container = this.getContainer();
    if (!container || !container.contains(event.target)) return;

    const category = event.target.dataset.category;
    if (!category) return;

    if (event.target.open) {
      this.openCategories.add(category);
    } else {
      this.openCategories.delete(category);
    }
  }

  handleVariantChange() {
    // Delay to allow Shopify section rendering to complete
    requestAnimationFrame(() => {
      setTimeout(() => this.restoreOpenStates(), 100);
    });
  }

  restoreOpenStates() {
    const container = this.getContainer();
    if (!container) return;

    // Set flag to prevent toggle events during restoration
    this.isRestoring = true;

    const details = container.querySelectorAll('details[data-category]');
    details.forEach(detail => {
      const category = detail.dataset.category;
      if (this.openCategories.has(category)) {
        detail.open = true;
      }
    });

    // Clear the flag after a brief delay
    requestAnimationFrame(() => {
      this.isRestoring = false;
    });
  }

  setupMutationObserver() {
    // Find a persistent parent element to observe
    // The main-product section or product-info container typically persists
    const observeTarget = document.querySelector('.product') ||
                          document.querySelector('[data-section-type="product"]') ||
                          document.querySelector('main') ||
                          document.body;

    const observer = new MutationObserver((mutations) => {
      // Check if any mutation added nodes that include our container
      const containerWasAdded = mutations.some(mutation => {
        if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) return false;

        return Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          // Check if the added node is or contains our specs container
          return node.matches?.(this.containerSelector) ||
                 node.querySelector?.(this.containerSelector);
        });
      });

      if (containerWasAdded && this.openCategories.size > 0) {
        // Container was replaced, restore states
        requestAnimationFrame(() => {
          setTimeout(() => this.restoreOpenStates(), 50);
        });
      }
    });

    observer.observe(observeTarget, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new GroupedProductSpecifications();
    new CollapsibleStatePreserver();
  });
} else {
  new GroupedProductSpecifications();
  new CollapsibleStatePreserver();
}

// Assign classes to window to prevent re-declaration errors
window.GroupedProductSpecifications = GroupedProductSpecifications;
window.CollapsibleStatePreserver = CollapsibleStatePreserver;

} // End of re-declaration guard

/**
 * Convert dimensions to fractions in any container (for quickview support)
 * Uses the same formatDimensionValue logic as the main class
 */
function convertDimensionsInContainer(container) {
  if (!container) return;

  // Target all value cells used in different metafield displays
  const valueCells = container.querySelectorAll('.specification-value, .spec-value, .metafield-table td:last-child');

  valueCells.forEach(cell => {
    const originalText = cell.textContent?.trim();
    if (!originalText) return;

    // Use the formatDimensionValue logic from GroupedProductSpecifications
    const result = formatDimensionValueStatic(originalText);
    if (result && typeof result === 'object' && result.html) {
      cell.innerHTML = result.value;
    } else if (result !== originalText) {
      cell.textContent = result;
    }
  });
}

/**
 * Static version of formatDimensionValue for use outside class context
 * Converts decimal dimensions to American fractions (1/64" precision)
 */
function formatDimensionValueStatic(value) {
  if (typeof value !== 'string') return value;

  // Skip weight values - return as-is without subscript styling
  const valueLower = value.toLowerCase();
  if (valueLower.includes('lb') || valueLower.includes('oz') ||
      valueLower.includes('kg') || valueLower.includes(' g') ||
      valueLower.endsWith('g')) {
    return value;
  }

  const getValue = (v) => (v && typeof v === 'object' && v.html) ? v.value : v;
  const isHtml = (v) => v && typeof v === 'object' && v.html;

  // Check for compound dimensions (e.g., "25.25 x 25.75" or "5 x 4.5 x 0.5"")
  if (/[xX×]/.test(value)) {
    const trailingInchMatch = value.match(/[""]$/);
    const trailingInch = trailingInchMatch ? trailingInchMatch[0] : '';
    const valueWithoutTrailing = trailingInch ? value.slice(0, -1) : value;

    const parts = valueWithoutTrailing.split(/(\s*[xX×]\s*)/);
    const convertedParts = [];
    let hasHtml = false;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (i % 2 === 0) {
        const dimMatch = part.match(/^([\d.]+)(\s*[""]?)$/);
        if (dimMatch) {
          const converted = convertSingleDimensionStatic(dimMatch[1], dimMatch[2]);
          if (isHtml(converted)) hasHtml = true;
          convertedParts.push(converted);
        } else {
          convertedParts.push(part);
        }
      } else {
        convertedParts.push(part);
      }
    }

    const result = convertedParts.map(getValue).join('') + trailingInch;
    return hasHtml ? { html: true, value: result } : result;
  }

  // Single dimension
  const match = value.match(/^([\d.]+)(\s*[""]?)(.*)$/);
  if (!match) return value;

  const result = convertSingleDimensionStatic(match[1], match[2]);
  const suffix = match[3] || '';

  if (isHtml(result)) {
    return { html: true, value: result.value + suffix };
  }
  return result + suffix;
}

/**
 * Static version of convertSingleDimension
 */
function convertSingleDimensionStatic(numStr, inchMark = '') {
  const num = parseFloat(numStr);
  if (isNaN(num)) return numStr + inchMark;

  const whole = Math.floor(num);
  const decimal = num - whole;

  if (decimal < 0.0078125) {
    return whole + inchMark;
  }

  const sixtyfourths = Math.round(decimal * 64);

  if (sixtyfourths === 64) {
    return (whole + 1) + inchMark;
  }

  const fraction = reduceFractionStatic(sixtyfourths, 64);

  if (whole === 0) {
    return { html: true, value: fraction.value + inchMark };
  }
  return { html: true, value: whole + '\u2009' + fraction.value + inchMark };
}

/**
 * Static version of reduceFraction
 */
function reduceFractionStatic(num, denom) {
  const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
  const divisor = gcd(num, denom);
  const reducedNum = num / divisor;
  const reducedDenom = denom / divisor;
  return { html: true, value: `<span class="fraction">${reducedNum}/${reducedDenom}</span>` };
}

// Listen for quickview loaded event to convert dimensions
document.addEventListener('quick-view:loaded', (event) => {
  // Small delay to ensure DOM is fully updated
  setTimeout(() => {
    const quickviewContent = document.querySelector('.quick-view__content');
    if (quickviewContent) {
      convertDimensionsInContainer(quickviewContent);
    }
  }, 100);
});